import React from 'react';
import { useState, useEffect } from 'react';
import produce from "immer";

import PuzzleMatrix from '../data/PuzzleMatrix';
import PuzzleRow from './PuzzleRow';
import PuzzleScorePopup from './PuzzleScorePopup';
import ListUtils from '../helpers/common/ListUtils';
import StartOrTryAgainPopup from './common/StartOrTryAgainPopup';
import DynamicsStyles from './DynamicsStyles';

/** @type {PuzzleDefinition} */
const hardcodedDefinition = {
    rows: 3,
    columns: 6,
    imageSrc: "image.jpg",
    spainImageSrc: "image.jpg",
    lockCorrectCells: true,
};

const hardcodedPuzzle = new PuzzleMatrix({
    columns: 1,
    rows: 1,
    imageSrc: "",
    spainImageSrc: "",
    lockCorrectCells: true,
});

/**
 *
 * @param {PuzzleArgs} param0
 */
const Puzzle = ({ api, progressTracker, resourceFetcher }) => {

    const [puzzleMatrix, setPuzzleMatrix] = useState(hardcodedPuzzle);

    const locale = navigator.language != undefined ? navigator.language.slice(0,2) : "it";

    /** @type {Array<*> & { 0: _Coordinates | undefined, 1: function(_Coordinates | undefined): void}} */
    // @ts-ignore
    const [selectedCoordinates, setSelectedCoordinates] = useState(undefined);

    const [ready, setReady] = useState(false);
    const [started, setStarted] = useState(false);
    const [completed, setCompleted] = useState(false);

    const [startError, setStartError] = useState("");

    const [stats, setStats] = useState({
        attempts: 0,
        seconds: 0,
    });

    const init = () => {
        setStartError("");
        let definition = hardcodedDefinition;

        return resourceFetcher.get("default-puzzle.json")
            .then(response => response.json())
            .then(json => { definition = json; })
            .catch(err => { console.error("error getting default puzzle", err); })
            .then(() => {
                /** @type {ResolvedPuzzleDefinition} */
                const resolvedDefinition = {
                    ...definition,
                    imageSrc: resourceFetcher.getCompleteResourceUrl(locale == "es" ? definition.spainImageSrc : definition.imageSrc),
                    image: undefined,
                    containerHeight: window.innerHeight,
                    containerWidth: window.innerWidth,
                };

                return resourceFetcher.getImage(locale == "es" ? definition.spainImageSrc : definition.imageSrc)
                    .then(img => {
                        resolvedDefinition.image = img;
                        const actualPuzzleMatrix = new PuzzleMatrix(resolvedDefinition);
                        actualPuzzleMatrix.shuffle();
                        setPuzzleMatrix(actualPuzzleMatrix);

                        setReady(true);
                    })
                    .catch(err => {
                        console.error("error getting puzzle image", err);
                        // TODO: localization
                        setStartError("could not find image " + resolvedDefinition.imageSrc);
                    });
            })
    };

    // init, this will execute only once
    useEffect(() => { init() }, []);

    useEffect(() => {
        // resize puzzle if needed when window is resized


        function resizePuzzle() {
          
            setPuzzleMatrix(produce(puzzleMatrix, draft => {
                draft.updateContainerDimensions(window.innerWidth, window.innerHeight);
            }));
        }

        window.addEventListener('resize', resizePuzzle);

        // useEffect will be called many times, we use the following to cleanup the previously added listener
        return () => {
            window.removeEventListener('resize', resizePuzzle);
        }
    });

    const onStart = () => {
        setStarted(true);
        progressTracker.setStartDate();
    };

    /** @type {PuzzleCellCallback} */
    const onCellClick = ({ cell, coordinates }) => {
        setPuzzleMatrix(produce(puzzleMatrix, draft => {
            if (selectedCoordinates === undefined) {
                draft.at(coordinates).selected = true;
                setSelectedCoordinates(coordinates);
            }
            else {
                // swap selected and clicked cell
                const selectedCell = draft.at(selectedCoordinates);
                const selectedSlice = selectedCell.imgSlice;

                const clickedCell = draft.at(coordinates);

                selectedCell.imgSlice = clickedCell.imgSlice;
                clickedCell.imgSlice = selectedSlice;

                // lock correct cells if necessary
                if (puzzleMatrix.definition.lockCorrectCells) {
                    if (selectedCell.areCoordinatesMatching(selectedCoordinates))
                        selectedCell.locked = true;
                    if (clickedCell.areCoordinatesMatching(coordinates))
                        clickedCell.locked = true;

                    if (selectedCell.locked || clickedCell.locked) {
                        const draftCompleted = draft.isCompleted();
                        progressTracker.saveSuccessfulAttempt(draftCompleted);

                        if (draftCompleted)
                            setTimeout(showScore, 1000);
                    }
                    else {
                        progressTracker.saveFailedAttempt(false);
                    }
                }

                selectedCell.selected = false;
                setSelectedCoordinates(undefined);
            }
        }));
    };

    const showScore = () => {
        setCompleted(true);
        setStats({
            attempts: progressTracker.progress.attempts,
            seconds: progressTracker.progress.time,
        });
    };

    return (
        <div className="puzzle-container">
            <DynamicsStyles resourceFetcher={resourceFetcher} />


            <h3 className="title">
                {/*Puzzle*/}
            </h3>

            <div
                className={ListUtils.toHtmlClasses([
                    "puzzle-board",
                    !started ? "puzzle-not-started" : ""
                ])}
            >
                {puzzleMatrix.rows.map((row, index) =>
                    <PuzzleRow
                        key={index}
                        index={index}
                        row={row}
                        puzzleDefinition={puzzleMatrix.definition}
                        onCellClick={onCellClick}
                    />
                )}
            </div>
            <div className="wrapper-intro-image" style={{display:started?'none':'flex'}}>
                    <StartOrTryAgainPopup
                        start={{
                            className: "puzzle-start",
                            show: !started,
                            disabled: !ready,
                            style: {
                                backgroundImage: `url('${locale == "es" ? puzzleMatrix.definition.spainImageSrc : puzzleMatrix.definition.imageSrc}')`,
                            },
                            onCloseClicked: onStart
                        }}
                        hasError={Boolean(startError)}
                        tryAgain={{
                            onTryAgainClicked: init
                        }}
                    >
                        <p>
                            Ooops! An error occured: {startError}
                        </p>
                    </StartOrTryAgainPopup>
            </div>

            <PuzzleScorePopup
                show={completed}
                stats={stats}
            />

        </div>
    );
}

export default Puzzle;
